import { useState, useEffect, useCallback } from 'react'
import { getSubscriptionStatus } from '../utils/api.js'

/** Delay (ms) before a loading spinner becomes visible. Prevents flashing on fast responses. */
const LOADER_DELAY_MS = 200

/**
 * Returns a boolean that becomes `true` only after `delayMs` of continuous loading.
 * Use this instead of `loading` when rendering spinners to avoid flicker on fast responses.
 */
export function useDelayedLoader(loading, delayMs = LOADER_DELAY_MS) {
  const [showLoader, setShowLoader] = useState(false)

  useEffect(() => {
    if (!loading) {
      setShowLoader(false)
      return
    }
    const timer = setTimeout(() => setShowLoader(true), delayMs)
    return () => clearTimeout(timer)
  }, [loading, delayMs])

  return showLoader
}

/**
 * Generic data fetching hook with loading, error, and refetch support.
 * Returns `showLoader` — a delayed loading flag (see `useDelayedLoader`) —
 * in addition to the raw `loading` boolean so callers can avoid spinner flicker.
 */
export function useFetch(fetchFn, deps = []) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const showLoader = useDelayedLoader(loading)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchFn()
      setData(res.data)
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Unknown error')
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    fetch()
  }, [fetch])

  return { data, loading, showLoader, error, refetch: fetch }
}

/**
 * Subscription status for a venue.
 */
export function useSubscriptionStatus(venueId, chatId = null) {
  const [subscribed, setSubscribed] = useState(false)
  const [loading, setLoading] = useState(true)

  const check = useCallback(async () => {
    if (!venueId) return
    setLoading(true)
    try {
      const res = await getSubscriptionStatus(venueId, chatId)
      setSubscribed(res.data.subscribed)
    } catch {
      setSubscribed(false)
    } finally {
      setLoading(false)
    }
  }, [venueId, chatId])

  useEffect(() => { check() }, [check])

  return { subscribed, loading, refetch: check }
}
